/**
 * Adaptive Fan Profile — local-first, lightweight orchestration.
 * Reuses existing in-app signals (predictions, polls, stats, sync, social,
 * reactions) to classify a fan personality, derive a Fan DNA badge and
 * advise adaptive timing for prediction / poll prompts.
 *
 * No realtime, no LLM, no network. Stored in localStorage. Demo-safe.
 */

import { useEffect, useState } from "react";

const STORAGE_KEY = "vs_adaptive_fan_profile_v1";

export type FanPersonality =
  | "tactical"
  | "emotional"
  | "social"
  | "casual"
  | "betting_like";

export type FanDna =
  | "Tactical Mind"
  | "Emotional Pulse"
  | "Crowd Leader"
  | "Ice Predictor"
  | "Momentum Hunter";

export interface AdaptiveFanProfile {
  favoriteTeam?: string;
  language?: string;
  /** raw counters of in-app signals */
  counters: {
    statsOpens: number;
    insightsOpens: number;
    syncOpens: number;
    pollVotes: number;
    predictionAnswers: number;
    reactions: number;
    socialOpens: number;
    sessionsCount: number;
    activeMinutes: number;
  };
  lastSeenAt: number;
  createdAt: number;
  /** cached classification, recomputed on update */
  personality: FanPersonality;
  dna: FanDna;
  /** confidence 0..1 of the personality call */
  confidence: number;
}

const DEFAULT_PROFILE: AdaptiveFanProfile = {
  counters: {
    statsOpens: 0,
    insightsOpens: 0,
    syncOpens: 0,
    pollVotes: 0,
    predictionAnswers: 0,
    reactions: 0,
    socialOpens: 0,
    sessionsCount: 0,
    activeMinutes: 0,
  },
  lastSeenAt: 0,
  createdAt: Date.now(),
  personality: "casual",
  dna: "Momentum Hunter",
  confidence: 0,
};

function safeRead(): AdaptiveFanProfile {
  if (typeof window === "undefined") return { ...DEFAULT_PROFILE };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PROFILE };
    const parsed = JSON.parse(raw) as Partial<AdaptiveFanProfile>;
    return {
      ...DEFAULT_PROFILE,
      ...parsed,
      counters: { ...DEFAULT_PROFILE.counters, ...(parsed.counters ?? {}) },
    };
  } catch {
    return { ...DEFAULT_PROFILE };
  }
}

function safeWrite(p: AdaptiveFanProfile) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* noop */
  }
}

/* ──────────────────── Personality + DNA derivation ──────────────────── */

export function classifyPersonality(
  c: AdaptiveFanProfile["counters"],
): { personality: FanPersonality; confidence: number } {
  const tactical = c.statsOpens * 2 + c.insightsOpens * 2 + c.syncOpens;
  const emotional = c.reactions * 2 + c.pollVotes;
  const social = c.socialOpens * 2;
  const betting = c.predictionAnswers * 1.5;
  const total = tactical + emotional + social + betting;

  if (total < 4) return { personality: "casual", confidence: 0.2 };

  const scores: Array<[FanPersonality, number]> = (
    [
      ["tactical", tactical],
      ["emotional", emotional],
      ["social", social],
      ["betting_like", betting],
    ] as Array<[FanPersonality, number]>
  ).sort((a, b) => b[1] - a[1]);

  const [top, topScore] = scores[0];
  const second = scores[1][1];
  const confidence = Math.max(0, Math.min(1, (topScore - second) / Math.max(1, topScore)));
  return { personality: top, confidence };
}

export function deriveDna(p: FanPersonality, c: AdaptiveFanProfile["counters"]): FanDna {
  if (p === "tactical") return "Tactical Mind";
  if (p === "emotional") return "Emotional Pulse";
  if (p === "social") return "Crowd Leader";
  if (p === "betting_like") {
    // High accuracy bias when predictionAnswers >> pollVotes & reactions
    return c.predictionAnswers > 12 ? "Ice Predictor" : "Momentum Hunter";
  }
  return "Momentum Hunter";
}

/* ──────────────────── Mutation API ──────────────────── */

export type FanEventKind =
  | "stats_open"
  | "insights_open"
  | "sync_open"
  | "poll_vote"
  | "prediction_answer"
  | "reaction"
  | "social_open"
  | "session_start";

const listeners = new Set<(p: AdaptiveFanProfile) => void>();

function recompute(p: AdaptiveFanProfile): AdaptiveFanProfile {
  const { personality, confidence } = classifyPersonality(p.counters);
  return { ...p, personality, confidence, dna: deriveDna(personality, p.counters) };
}

export function recordFanEvent(kind: FanEventKind, by = 1): AdaptiveFanProfile {
  const p = safeRead();
  switch (kind) {
    case "stats_open":         p.counters.statsOpens += by; break;
    case "insights_open":      p.counters.insightsOpens += by; break;
    case "sync_open":          p.counters.syncOpens += by; break;
    case "poll_vote":          p.counters.pollVotes += by; break;
    case "prediction_answer":  p.counters.predictionAnswers += by; break;
    case "reaction":           p.counters.reactions += by; break;
    case "social_open":        p.counters.socialOpens += by; break;
    case "session_start":      p.counters.sessionsCount += by; break;
  }
  p.lastSeenAt = Date.now();
  const next = recompute(p);
  safeWrite(next);
  listeners.forEach((fn) => { try { fn(next); } catch { /* noop */ } });
  return next;
}

export function setFanContext(ctx: { favoriteTeam?: string; language?: string }): AdaptiveFanProfile {
  const p = safeRead();
  if (ctx.favoriteTeam !== undefined) p.favoriteTeam = ctx.favoriteTeam;
  if (ctx.language !== undefined) p.language = ctx.language;
  const next = recompute(p);
  safeWrite(next);
  listeners.forEach((fn) => { try { fn(next); } catch { /* noop */ } });
  return next;
}

export function getFanProfile(): AdaptiveFanProfile {
  return recompute(safeRead());
}

export function resetFanProfile(): AdaptiveFanProfile {
  const fresh = { ...DEFAULT_PROFILE, createdAt: Date.now() };
  safeWrite(fresh);
  listeners.forEach((fn) => { try { fn(fresh); } catch { /* noop */ } });
  return fresh;
}

export function subscribeFanProfile(fn: (p: AdaptiveFanProfile) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/* ──────────────────── React hook ──────────────────── */

export function useAdaptiveFanProfile(): AdaptiveFanProfile {
  // SSR-safe: start with DEFAULT_PROFILE so server and first client render match.
  // Hydrate real localStorage values inside useEffect (client-only).
  const [profile, setProfile] = useState<AdaptiveFanProfile>(() => ({
    ...DEFAULT_PROFILE,
    counters: { ...DEFAULT_PROFILE.counters },
  }));
  useEffect(() => {
    setProfile(getFanProfile());
    return subscribeFanProfile(setProfile);
  }, []);
  return profile;
}

/* ──────────────────── Adaptive timing advisor ──────────────────── */

export interface PredictionTimingAdvice {
  /** in-match minutes between contextual prediction prompts */
  interval: number;
  /** type of question pool to favor */
  pool: "tactical" | "emotional" | "social" | "balanced";
}

export function advisePredictionTiming(p: AdaptiveFanProfile): PredictionTimingAdvice {
  switch (p.personality) {
    case "tactical":     return { interval: 5,  pool: "tactical"  };
    case "emotional":    return { interval: 4,  pool: "emotional" };
    case "social":       return { interval: 6,  pool: "social"    };
    case "betting_like": return { interval: 3,  pool: "balanced"  };
    case "casual":
    default:             return { interval: 10, pool: "emotional" };
  }
}

/* ──────────────────── Display helpers ──────────────────── */

export const PERSONALITY_META: Record<FanPersonality, { label: string; emoji: string; color: string; tagline: string }> = {
  tactical:     { label: "Tactical Fan",     emoji: "🧠", color: "oklch(0.78 0.18 240)", tagline: "Lees el juego entre líneas." },
  emotional:    { label: "Emotional Fan",    emoji: "❤️", color: "oklch(0.7 0.2 25)",    tagline: "Vives cada minuto al máximo." },
  social:       { label: "Social Fan",       emoji: "📣", color: "oklch(0.78 0.18 145)", tagline: "Tu voz mueve la comunidad." },
  casual:       { label: "Casual Fan",       emoji: "🎟️", color: "oklch(0.7 0.05 250)",  tagline: "Disfrutas a tu ritmo." },
  betting_like: { label: "Predictor Fan",    emoji: "🔮", color: "oklch(0.78 0.2 280)",  tagline: "Adelantas las jugadas." },
};

export const DNA_META: Record<FanDna, { emoji: string; color: string; tagline: string }> = {
  "Tactical Mind":   { emoji: "🧠", color: "oklch(0.78 0.18 240)", tagline: "Ves patrones donde otros ven caos." },
  "Emotional Pulse": { emoji: "❤️", color: "oklch(0.7 0.2 25)",    tagline: "Tu corazón late con el partido." },
  "Crowd Leader":    { emoji: "📣", color: "oklch(0.78 0.18 145)", tagline: "Mueves la conversación." },
  "Ice Predictor":   { emoji: "🧊", color: "oklch(0.78 0.16 220)", tagline: "Frío. Preciso. Letal." },
  "Momentum Hunter": { emoji: "🔥", color: "oklch(0.78 0.2 50)",   tagline: "Cazas la ola del momentum." },
};
